/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/react-native
 * @description Renders a compact native status badge for adapter lifecycle
 * state using View/Text and testID selectors.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "react";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";
import { semanticColors } from "../bridges/TokenBridge";
import { StyleSheet, Text, View } from "../lib/native";

const statusLabels: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

type StatusColors = {
  backgroundColor: string;
  borderColor: string;
  color: string;
};

const disconnectedColors: StatusColors = {
  backgroundColor: semanticColors.status.disconnected.bg,
  borderColor: semanticColors.status.disconnected.border,
  color: semanticColors.status.disconnected.text,
};

const statusColors: Record<AdapterStatusType, StatusColors> = {
  connected: {
    backgroundColor: semanticColors.status.connected.bg,
    borderColor: semanticColors.status.connected.border,
    color: semanticColors.status.connected.text,
  },
  disconnected: disconnectedColors,
  error: {
    backgroundColor: semanticColors.status.error.bg,
    borderColor: semanticColors.status.error.border,
    color: semanticColors.status.error.text,
  },
  initializing: {
    backgroundColor: semanticColors.status.initializing.bg,
    borderColor: semanticColors.status.initializing.border,
    color: semanticColors.status.initializing.text,
  },
};

/**
 * Props for the native adapter status badge.
 *
 * @property showLabel Whether to render the text label next to the status dot.
 * @property size Visual size for the badge.
 * @property status Adapter lifecycle status to display.
 * @property style Optional native style merged onto the badge container.
 * @property textStyle Optional native text style merged onto the label.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface AdapterStatusBadgeProps {
  showLabel?: boolean;
  size?: "sm" | "md";
  status: AdapterStatusType;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  testID?: string;
}

/**
 * Renders a compact native badge for an adapter lifecycle status.
 *
 * @param props Badge props including status, size, and visibility options.
 * @returns A native status badge with semantic colors and accessibility label.
 * @example
 * ```tsx
 * <AdapterStatusBadge status="connected" size="md" />
 * ```
 */
export function AdapterStatusBadge({
  status,
  showLabel = true,
  size = "sm",
  style,
  textStyle,
  testID,
}: AdapterStatusBadgeProps): JSX.Element {
  const colors = statusColors[status] ?? disconnectedColors;
  const label = statusLabels[status] ?? status;

  return (
    <View
      accessibilityLabel={`Adapter status: ${label}`}
      accessibilityRole="text"
      style={[
        styles.badge,
        size === "md" && styles.badgeMd,
        {
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor,
        },
        style,
      ]}
      testID={testID ?? `adapter-status-${status}`}
    >
      <View style={[styles.dot, { backgroundColor: colors.color }]} />
      {showLabel && (
        <Text
          style={[
            styles.label,
            size === "md" && styles.labelMd,
            { color: colors.color },
            textStyle,
          ]}
        >
          {label}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeMd: {
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  dot: {
    borderRadius: 999,
    height: 6,
    width: 6,
  },
  label: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  labelMd: {
    fontSize: 12,
  },
});
